'use client'
import * as React from 'react'
import * as Dialog from 'primitives/dialog'
import { IconClose } from '@repo/design-system-cf/components/icons'
import Image from 'next/image'
import { Z_INDEX_MODALS } from 'constants/modals'
import { useFeaturedImagesDialog } from '../contexts/featured-images-dialog-context'
import { useImageLibraryInfiniteQuery } from '../../_hooks/use-image-library-infinite'
import { Spinner } from '@repo/design-system-cf/components/spinner'
import type { ListImageLibraryResponse } from '@web/gen/service/creativefabrica/social/v2/service_pb'
import type { InfiniteData } from '@tanstack/react-query'
import {
  useGetUserFeaturedImagesQuery,
  useSelectFeaturedImagesMutation,
  GetUserFeaturedImagesQueryRootKey,
} from '@web/gen/service/creativefabrica/social/v2'
import { useFeatureImageUpload } from '../../_hooks/use-feature-image-upload'
import { useBreakpoint } from '@repo/web-ui/hooks/use-breakpoints'
import { useQueryClient } from '@tanstack/react-query'
import { useParams } from 'next/navigation'
import { Link } from '@repo/design-system-cf/components/link'

export function FeaturedImagesDialog() {
  // const t = useTranslations('social-feed.page.crafter_profile')
  const { breakpoint } = useBreakpoint()
  const isMobile = ['xs', 'sm'].includes(breakpoint ?? '')
  const { isOpen, closeDialog, imageIndex } = useFeaturedImagesDialog()
  const { slug } = useParams()

  const scrollRef = React.useRef<HTMLDivElement>(null)

  const {
    data: imageLibraryData,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch: refetchImageLibrary,
  } = useImageLibraryInfiniteQuery({
    itemsPerPage: 20,
    enabled: isOpen,
  })

  const queryClient = useQueryClient()
  const { data: userFeaturedImages } = useGetUserFeaturedImagesQuery(
    {
      userSlug: slug as string,
    },
    {
      enabled: Boolean(slug),
    },
  )

  const { uploadFeaturedImage, loading: uploadLoading } = useFeatureImageUpload()

  const fileInputRef = React.useRef<HTMLInputElement>(null)
  // const [uploadedImage, setUploadedImage] = React.useState<{ url: string; id: string } | null>(null)

  const images = React.useMemo(() => {
    const data = imageLibraryData as InfiniteData<ListImageLibraryResponse> | undefined
    const allImages =
      data?.pages?.flatMap((page: ListImageLibraryResponse) => page?.images ?? []) ?? []

    // Get IDs of currently selected featured images
    const selectedImageIds = new Set(
      // oxlint-disable-next-line unicorn/no-useless-collection-argument
      userFeaturedImages?.images?.map(img => img?.id).filter(Boolean) ?? [],
    )

    // Filter out currently selected images
    return allImages.filter(image => image?.id && !selectedImageIds.has(image.id))
  }, [imageLibraryData, userFeaturedImages])

  const tryLoadMore = React.useCallback(
    async (element?: HTMLDivElement | null) => {
      const scrollElement = element ?? scrollRef.current
      if (!scrollElement) {
        return
      }

      const distanceFromBottom =
        scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight

      if (distanceFromBottom > 60) {
        return
      }

      if (isFetchingNextPage || !hasNextPage) {
        return
      }

      await fetchNextPage()
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  const handleScroll = React.useCallback(
    async (event: React.UIEvent<HTMLDivElement>) => {
      await tryLoadMore(event.currentTarget)
    },
    [tryLoadMore],
  )

  React.useEffect(() => {
    if (!isOpen) {
      return
    }

    if (!hasNextPage) {
      return
    }

    const scrollElement = scrollRef.current
    void tryLoadMore(scrollElement)
  }, [isOpen, hasNextPage, tryLoadMore, images.length])

  const selectImageMutation = useSelectFeaturedImagesMutation()

  const handleSelectImage = (imageId: string | undefined) => {
    if (!imageId) {
      return
    }

    // Get all current featured images
    const currentImages = userFeaturedImages?.images ?? []

    // Create a map of position to imageId for all current images
    const imagesByPosition = new Map<number, string>()
    currentImages.forEach(image => {
      if (image?.position && image?.id) {
        imagesByPosition.set(image.position, image.id)
      }
    })

    // Build the images array with all positions (1, 2, 3)

    // Keep existing images in their positions, replace the target position
    const imagesToSend = []

    for (let position = 1; position <= 3; position++) {
      if (position === imageIndex) {
        // Replace the image at the target position
        imagesToSend.push({
          imageId,
          order: position,
        })
      } else {
        // Keep the existing image at this position
        const existingImageId = imagesByPosition.get(position) ?? ''
        if (existingImageId) {
          imagesToSend.push({
            imageId: existingImageId,
            order: position,
          })
        }
      }
    }

    selectImageMutation.mutate(
      {
        images: imagesToSend,
      },
      {
        onSuccess: () => {
          // Invalidate all getUserFeaturedImages queries to trigger refetch in all components
          void queryClient
            .invalidateQueries({
              queryKey: [GetUserFeaturedImagesQueryRootKey],
            })
            .then(() => {
              closeDialog()
            })
        },
      },
    )
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    try {
      // Upload the featured image and wait for the notification
      const notification = await uploadFeaturedImage(file)

      // Extract image URL from notification
      const upload = notification?.social?.feedPayload?.uploadProcessed?.upload

      if (upload) {
        // setUploadedImage({
        //   url: upload?.image?.url ?? '',
        //   id: upload?.id ?? '',
        // })
      }
      // Refetch the image library to show the newly uploaded image
      await refetchImageLibrary()

      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to upload image:', error)
      // setUploadedImage(null)
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && closeDialog()} defaultOpen>
      <React.Suspense fallback={null}>
        <Dialog.Portal>
          <Dialog.Body
            placement={isMobile ? 'bottom' : 'middle'}
            className="font-poppins z-50 xl:overflow-y-auto xl:py-10"
            overlayZIndex={Z_INDEX_MODALS.default}
            zIndex={Z_INDEX_MODALS.default}
          >
            <div className="bg-cf-white rounded-4 relative w-full max-w-[756px] lg:max-w-[880px] p-5 tablet:p-8">
              <h2 className="text-20 text-cf-grey-1000 font-bold pb-4">Select a project</h2>
              <p className="text-14 text-cf-grey-800 mb-6">
                {'Add a project you’ve posted as a featured or'}
                <Link
                  variant="primary"
                  className="ml-1 cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {' upload a photo instead.'}
                </Link>
              </p>

              {/* DEBUG */}
              {/* File Upload Section */}
              <input
                ref={fileInputRef}
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploadLoading}
                className="hidden"
              />

              {isLoading && images.length === 0 && !uploadLoading ? (
                <div className="flex items-center justify-center py-8 min-h-[400px]">
                  <p className="text-16 text-cf-sf-gray-100">Loading images...</p>
                </div>
              ) : (
                <>
                  <div
                    ref={scrollRef}
                    onScroll={handleScroll}
                    className="grid grid-cols-3 gap-2 max-h-[65vh] tablet:max-h-[80vh] pb-8 overflow-y-auto"
                  >
                    {uploadLoading ? (
                      <div className="col-span-1 aspect-[3/2] rounded-[12px] bg-cf-grey-100 flex items-center justify-center">
                        <div className="flex flex-col items-center">
                          <Spinner className="size-8" />
                        </div>
                      </div>
                    ) : null}

                    {images.length === 0 && !uploadLoading ? (
                      <p className="col-span-full text-16 text-cf-sf-gray-100 pb-8">
                        No featured images available.
                      </p>
                    ) : (
                      images.map(
                        (image: { id?: string; optimizedImageUrl?: string }, index: number) => (
                          <div key={image?.id || index} className="col-span-1 aspect-[3/2]">
                            <Image
                              src={image?.optimizedImageUrl ?? ''}
                              alt={`Featured Image ${index + 1}`}
                              width={300}
                              height={300}
                              className="w-full h-full rounded-[12px] cursor-pointer object-cover"
                              onClick={() => handleSelectImage(image?.id)}
                            />
                          </div>
                        ),
                      )
                    )}
                    {hasNextPage ? (
                      <div className="p-4 col-span-full  flex items-center justify-center">
                        {isFetchingNextPage ? <Spinner /> : null}
                      </div>
                    ) : null}
                  </div>
                </>
              )}

              <Dialog.Close
                className="hover:bg-text-hover text-cf-purple-900 z-100 absolute right-5 top-4 flex h-6 w-6 items-center justify-center rounded-full font-bold"
                onClick={closeDialog}
              >
                <IconClose className="h-full w-full" />
              </Dialog.Close>
            </div>
          </Dialog.Body>
        </Dialog.Portal>
      </React.Suspense>
    </Dialog.Root>
  )
}
